import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  IconButton,
  Table,
  Collapse,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Card,
  CardContent,
  useTheme,
  alpha,
  Fade,
  Chip,
  Avatar,
  Snackbar,
  Alert
} from '@mui/material';
import dayjs from 'dayjs';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Calculate as CalculateIcon,
  Receipt as ReceiptIcon,
  Description as DescriptionIcon,
  AttachMoney as AttachMoneyIcon,
  Business as BusinessIcon,
  DateRange as DateRangeIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Edit,
  KeyboardArrowDown,
  KeyboardArrowUp

} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { fr } from 'date-fns/locale';
import { format } from 'date-fns';
import factureService from '../../API/factureService';
import AlertBar from '../../Components/AlertBar';
import { motion } from 'framer-motion';
import AutocompleteComboBox from '../../Components/AutocompleteComboBox';
import ButtonAttribute from '../../Components/ButtonAttribute';
import DateTimePickerComponent from '../../Components/DateTimePicker';
import TextInputMui from '../../Components/TextInputMui';

const FactureForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const theme = useTheme();

  // États pour le formulaire
  const [formData, setFormData] = useState({
    ID_Client: '',
    ID_Voyage: '',
    numero_facture: '',
    date_facture: new Date(),
    periode_debut: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    periode_fin: new Date(),
    statut: 'brouillon',
    notes: '',
    conditions_paiement: 'Paiement à 30 jours',
    delai_paiement: 30,
    lignes: []
  });

  // États pour les données de référence
  const [clients, setClients] = useState([]);
  const [ClientFacture, setClientFacture] = useState({});
  const [voyagesNonFactures, setVoyagesNonFactures] = useState([]);
  const [lingesVoyagesNonFactures, setLingesVoyagesNonFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'info' });
  const [openVoyages, setOpenVoyages] = useState({});
  const toggleVoyage = (id) => {
    setLingesVoyagesNonFactures(voyagesNonFactures.find(v => v.ID_Voyage == id).lignes_voyage);
    setOpenVoyages((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };
  // États pour les dialogues
  const [addVoyageDialogOpen, setAddVoyageDialogOpen] = useState(false);
  const [selectedVoyages, setSelectedVoyages] = useState([]);
  const [resetSnackbar, setResetSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [isResetting, setIsResetting] = useState(false);
  const [voyageLinesMap, setVoyageLinesMap] = useState({});
  // Options pour les statuts
  const statutOptions = [
    { value: 'brouillon', label: 'Brouillon', color: 'default' },
    { value: 'envoyée', label: 'Envoyée', color: 'primary' },
    { value: 'payée', label: 'Payée', color: 'success' }
  ];

  // Charger les données initiales
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);

        if (isEditMode) {
          // Mode édition: charger la facture existante
          const factureData = await factureService.getFacture(id);

          // Formater les dates
          const formattedData = {
            ...factureData,
            date_facture: factureData.date_facture ? new Date(factureData.date_facture) : new Date(),
            periode_debut: factureData.periode_debut ? new Date(factureData.periode_debut) : new Date(),
            periode_fin: factureData.periode_fin ? new Date(factureData.periode_fin) : new Date(),
            date_echeance: factureData.date_echeance ? new Date(factureData.date_echeance) : null
          };

          setFormData(formattedData);

          // Charger les voyages non facturés pour ce client
          if (factureData.ID_Client) {
            const mois = new Date(factureData.periode_debut).getMonth() + 1;
            const annee = new Date(factureData.periode_debut).getFullYear();
            const voyages = await factureService.getNonFacturedVoyages(factureData.ID_Client, mois, annee);
            const lingesVoyages = voyages.lignes_voyage;

            setVoyagesNonFactures(voyages);
            setLingesVoyagesNonFactures(lingesVoyages);

          }
          const createData = await factureService.getCreateData();
          console.log('Data :', createData.clients)
          setClients(createData.clients);

        } else {
          // Mode création: charger les données pour créer une nouvelle facture
          const createData = await factureService.getCreateData();
          setClients(createData.clients);

          // Définir le numéro de facture généré automatiquement
          setFormData(prev => ({
            ...prev,
            numero_facture: createData.numero_facture
          }));
        }

        setLoading(false);
      } catch (error) {
        setAlert({
          open: true,
          message: 'Erreur lors du chargement des données',
          severity: 'error'
        });
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [id, isEditMode]);

  // Charger les voyages non facturés lorsque le client change
  const fetchVoyagesNonFactures = useCallback(async (clientId) => {
    if (!clientId) return;

    try {
      const mois = formData.periode_debut.getMonth() + 1;
      const annee = formData.periode_debut.getFullYear();

      const voyages = await factureService.getNonFacturedVoyages(clientId, mois, annee);

      setVoyagesNonFactures(voyages);
      setLingesVoyagesNonFactures(voyages.lignes_voyage);
    } catch (error) {
      setAlert({
        open: true,
        message: 'Erreur lors du chargement des voyages',
        severity: 'error'
      });
    }
  }, [formData.periode_debut]);

  // Réinitialiser les voyages non facturés
  const handleResetNonInvoicedVoyages = async () => {
    try {
      setIsResetting(true);
      const result = await factureService.resetNonInvoicedVoyages();

      setResetSnackbar({
        open: true,
        message: 'Voyages non facturés réinitialisés avec succès',
        severity: 'success'
      });

      // Recharger les voyages si un client est sélectionné
      if (formData.ID_Client) {
        await fetchVoyagesNonFactures(formData.ID_Client);
      }
    } catch (error) {
      setResetSnackbar({
        open: true,
        message: 'Erreur lors de la réinitialisation des voyages',
        severity: 'error'
      });
    } finally {
      setIsResetting(false);
    }
  };

  useEffect(() => {

    if (formData.ID_Client) {
      fetchVoyagesNonFactures(formData.ID_Client);
    }
  }, [formData.ID_Client, formData.periode_debut, fetchVoyagesNonFactures]);

  // Gérer les changements dans le formulaire
  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (name, date) => {
    setFormData(prev => ({ ...prev, [name]: date }));
  };

  const handleClientChange = async (event) => {
    const clientId = event.target.value;
    const clientData = await factureService.getFacturesByClientId(clientId);
    setClientFacture(clientData.data);
    console.log('clientData :', clientData);
    setFormData(prev => ({ ...prev, ID_Client: clientId }));
  };

  // Gérer les lignes de facture
  const handleAddLigne = () => {
    const newLigne = {
      ID_Ligne: null,
      ID_Facture: id || null,
      ID_Voyage: formData.lignes && formData.lignes.length > 0 ? formData.lignes[0].ID_Voyage : null,
      description: '',
      quantite: 1,
      prix_unitaire: null,
      montant_ht: null,
      taux_tva: null, // Always set to 20%
      taux_tva_1: null,
      montant_tva: null,
      montant_ttc: null
    };
    setFormData(prev => ({
      ...prev,
      lignes: [...prev.lignes, newLigne]
    }));
  };

  const handleDeleteLigne = (index) => {
    setFormData(prev => ({
      ...prev,
      lignes: prev.lignes.filter((_, i) => i !== index)
    }));
  };

  const handleLigneChange = (index, field, value) => {
    setFormData(prev => {
      const newLignes = [...prev.lignes];
      newLignes[index] = { ...newLignes[index], [field]: value };

      // Recalculer les montants si nécessaire
      if (field == 'quantite' || field == 'prix_unitaire' || field == 'taux_tva') {
        const ligne = newLignes[index];
        const montantHT = ligne.quantite * ligne.prix_unitaire;
        const montantTVA = montantHT * ((ligne.taux_tva + ligne.taux_tva_1) / 100);
        const montantTTC = montantHT + montantTVA;

        newLignes[index] = {
          ...ligne,
          montant_ht: montantHT,
          montant_tva: montantTVA,
          montant_ttc: montantTTC
        };
      }

      return { ...prev, lignes: newLignes };
    });
  };

  // Calculer les totaux
  const calculateTotals = () => {

    let montantHT = 0;
    let montantTVA = 0;
    let montantTTC = 0;

    const processLine = (ligne) => {

      const ht = Number(ligne?.montant_ht || 0);

      const tauxTVA = Number(ligne?.taux_tva || 0);
      const tauxTVA1 = Number(ligne?.taux_tva_1 || 0);

      const tva = ht * tauxTVA / 100;
      const tva1 = ht * tauxTVA1 / 100;

      const totalTVA = tva + tva1;
      const ttc = ht + totalTVA;

      montantHT += ht;
      montantTVA += totalTVA;
      montantTTC += ttc;
    };

    // MAIN FACTURE LINES
    formData.lignes?.forEach(processLine);

    // GROUPAGE LINES
    Object.values(voyageLinesMap || {}).forEach((lines) => {
      lines?.forEach(processLine);
    });

    return { montantHT, montantTVA, montantTTC };
  };

  // Gérer l'ajout de voyages
  const handleOpenAddVoyageDialog = () => {
    setAddVoyageDialogOpen(true);
    setSelectedVoyages([]);
    handleClientChange({ target: { value: formData.ID_Client } });
  };

  const handleVoyageSelectionChange = (voyage) => {

    const isSelected = selectedVoyages.some(
      (v) => v.ID_Voyage == voyage.ID_Voyage
    );

    if (isSelected) {
      // remove voyage
      setSelectedVoyages(prev =>
        prev.filter(v => v.ID_Voyage !== voyage.ID_Voyage)
      );

    } else {
      // add voyage
      setSelectedVoyages(prev => [...prev, voyage]);
    }

  };

  const handleAddSelectedVoyages = async () => {

    const newVoyageLinesMap = { ...voyageLinesMap };

    const newLignes = await Promise.all(
      selectedVoyages.map(async (voyage) => {

        const clientData = await factureService.getFacturesByClientId(
          voyage.Client_facturation
        );

        const tauxTVA = parseFloat(clientData.data?.taux_de_TVA) || 0;
        const tauxTVA1 = parseFloat(clientData.data?.taux_de_TVA_1) || 0;

        const montantHT = parseFloat(voyage.prix_total_voyage) || 0;

        const montantTVA = montantHT * ((tauxTVA + tauxTVA1) / 100);

        const montantTTC = montantHT + montantTVA;

        const debut = dayjs(voyage.Date_chargement);
        const fin = dayjs(voyage.Date_déchargement);

        const formattedDebut = debut.isValid()
          ? debut.format("DD/MM/YYYY")
          : "Date inconnue";

        const formattedFin = fin.isValid()
          ? fin.format("DD/MM/YYYY")
          : "Date inconnue";

        // GROUPAGE LINES
        const lignesVoyage = (lingesVoyagesNonFactures || [])
          .filter(l => l.voyage_principal_id == voyage.ID_Voyage)
          .map(l => ({
            ...l,
            quantite: 1,
            prix_unitaire: 0,
            taux_tva: tauxTVA,
            taux_tva_1: tauxTVA1,
            montant_ht: 0,
            montant_tva: 0,
            montant_ttc: 0
          }));

        newVoyageLinesMap[voyage.ID_Voyage] = lignesVoyage;

        return {
          ID_Voyage: voyage.ID_Voyage,
          description: `Transport - ${voyage.Type_Voyage} - ${formattedDebut} au ${formattedFin}`,
          quantite: 1,
          prix_unitaire: montantHT,
          taux_tva: tauxTVA,
          taux_tva_1: tauxTVA1,
          montant_ht: montantHT,
          montant_tva: montantTVA,
          montant_ttc: montantTTC,
          est_edite_manuellement: false,
          voyage_type: voyage.Type_Voyage
        };
      })
    );

    setVoyageLinesMap(newVoyageLinesMap);

    setFormData(prev => ({
      ...prev,
      lignes: [...prev.lignes, ...newLignes]
    }));

    setAddVoyageDialogOpen(false);
  };

  const handleVoyageLineChange = (voyageId, lineIndex, field, value) => {
    setVoyageLinesMap(prev => {

      const updated = { ...prev };
      const lines = [...updated[voyageId]];

      lines[lineIndex] = {
        ...lines[lineIndex],
        [field]: value
      };

      if (
        field === "quantite" ||
        field === "prix_unitaire" ||
        field === "taux_tva" ||
        field === "taux_tva_1"
      ) {

        const ligne = lines[lineIndex];

        const quantite = Number(ligne.quantite || 0);
        const prix = Number(ligne.prix_unitaire || 0);
        const tauxTVA = Number(ligne.taux_tva || 0);
        const tauxTVA1 = Number(ligne.taux_tva_1 || 0);

        const montantHT = quantite * prix;

        const tva = montantHT * tauxTVA / 100;
        const tva1 = montantHT * tauxTVA1 / 100;

        const montantTVA = tva + tva1;
        const montantTTC = montantHT + montantTVA;

        lines[lineIndex] = {
          ...ligne,
          montant_ht: montantHT,
          montant_tva: montantTVA,
          montant_ttc: montantTTC
        };
      }

      updated[voyageId] = lines;

      return updated;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);

      const { montantHT, montantTVA, montantTTC } = calculateTotals();

      // 1️⃣ MAIN FACTURE LINES
      const mainLignes = formData.lignes.map(l => ({
        ...l,
        ID_Voyage:
          l.ID_Voyage && !isNaN(l.ID_Voyage)
            ? Number(l.ID_Voyage)
            : null,
        type_ligne: "VOYAGE"
      }));


      // 2️⃣ GROUPAGE LINES → convert to facture lines
      const groupageLignes = [];

      Object.entries(voyageLinesMap).forEach(([voyageId, lines]) => {
        lines.forEach(ligne => {

          if (!ligne.prix_unitaire || ligne.prix_unitaire == 0) return;

          groupageLignes.push({
            ID_Voyage: Number(voyageId),
            description: `${ligne.expediteur} → ${ligne.destinataire}`,
            quantite: ligne.quantite,
            prix_unitaire: ligne.prix_unitaire,
            taux_tva: ligne.taux_tva,
            taux_tva_1: ligne.taux_tva_1,
            montant_ht: ligne.montant_ht,
            montant_tva: ligne.montant_tva,
            montant_ttc: ligne.montant_ttc,
            type_ligne: "GROUPAGE"
          });

        });
      });


      // 3️⃣ MERGE ALL LINES
      const allLignes = [...mainLignes, ...groupageLignes];


      const factureData = {
        ...formData,
        date_facture: formData.date_facture,
        periode_debut: formData.periode_debut,
        periode_fin: formData.periode_fin,

        lignes: allLignes,

        montant_ht: montantHT,
        montant_tva: montantTVA,
        montant_ttc: montantTTC
      };

      console.log("FACTURE FINAL PAYLOAD", factureData);

      let response;

      if (isEditMode) {
        response = await factureService.updateFacture(id, factureData);
      } else {
        response = await factureService.createFacture(factureData);
      }

      setAlert({
        open: true,
        message: `Facture ${isEditMode ? "mise à jour" : "créée"} avec succès`,
        severity: "success"
      });

      setTimeout(() => {
        navigate(`/factures/view/${response.ID_Facture || id}`);
      }, 1500);

    } catch (error) {

      console.error("Erreur soumission facture :", error);

      setAlert({
        open: true,
        message: `Erreur lors de la ${isEditMode ? "mise à jour" : "création"} de la facture`,
        severity: "error"
      });

      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/factures');
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f8f9fa'
      }}>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 2
          }}
        >
          <ReceiptIcon sx={{ fontSize: 60, color: theme.palette.primary.main, mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 3 }}>
            Chargement du formulaire de facture...
          </Typography>
          <CircularProgress />
        </Paper>
      </Box>
    );
  }

  const { montantHT, montantTVA, montantTTC } = calculateTotals();

  return (
    <>
      <Box sx={{
        padding: { xs: '20px', md: '20px' },
        backgroundColor: "#f5f5f5",
        transition: "padding 0.3s ease-in-out",
        minHeight: '116vh'
      }}>
        <AlertBar
          open={alert.open}
          onClose={() => setAlert({ ...alert, open: false })}
          textContent={alert.message}
          alertColor={alert.severity}
          autoHideDuration={4000}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mb: 3,
            mt: { xs: 2, sm: 2 },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 3 }
          }}>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }} >
              <ButtonAttribute
                type="button"
                Bgcolor={alpha(theme.palette.primary.main, 0.1)}
                color={theme.palette.primary.main}
                bgColorHover={alpha(theme.palette.primary.main, 0.2)}
                ColorHover={theme.palette.primary.main}
                startIcon={<ArrowBackIcon />}
                Label="Retour"
                action={handleBack}
                width="auto"
                height="45px"
                sx={{

                  fontWeight: 500,
                  px: 2,
                  py: 1
                }}
              />

              <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                {isEditMode ? 'Modifier la facture' : 'Nouvelle facture'}
              </Typography>
            </Box>
            <ButtonAttribute
              type="button"
              Bgcolor="#1976d2"
              color="#fff"
              bgColorHover="#115293"
              ColorHover="#fff"
              startIcon={
                saving ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              Label={saving ? "Enregistrement..." : "Enregistrer"}
              action={handleSubmit}
              disabled={saving}
              width="auto"
              height="45px"
              sx={{
                px: 3,
                py: 1,
                fontWeight: 500
              }}
            />

          </Box>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>

              {/* Informations générales */}
              <Grid item xs={12}>
                <Card elevation={3} sx={{
                  overflow: 'visible',
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb4b',
                  borderRadius: '32px',
                  padding: { xs: '20px', md: '30px' },
                  marginBottom: '20px',
                  boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)"
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <InfoIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="h6" fontWeight="bold">Informations générales</Typography>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    <Grid container spacing={3}>

                      {/* Client */}
                      <Grid item xs={12} sm={6} md={3}>
                        <AutocompleteComboBox
                          list={clients}
                          Label={isEditMode ? "Client (Modifier)" : "Client"}
                          required
                          keyField="ID_Client"
                          valueField="Raison_sociale"
                          value={formData.ID_Client ? Number(formData.ID_Client) : null}
                          dueShowValue
                          onChange={(selected) => {
                            // AutocompleteComboBox in your code returns the selected id / object depending on props.
                            // Here we expect a simple ID value (as earlier usage).
                            const newVal = typeof selected === 'object' && selected['ID_Client'] ? selected['ID_Client'] : selected;
                            setFormData({ ...formData, ID_Client: newVal });
                          }}
                          widthSpecific="100%"
                        />
                      </Grid>

                      {/* Numéro de facture */}
                      <Grid item xs={12} sm={6} md={3}>
                        <TextInputMui
                          label="Numéro de facture"
                          value={formData.numero_facture}
                          setValue={(val) => handleChange({ target: { name: 'numero_facture', value: val } })}
                          required
                          disabled={isEditMode}
                          type="text"
                        />
                      </Grid>

                      {/* Date de facture */}
                      <Grid item xs={12} sm={6} md={3}>
                        <DateTimePickerComponent
                          label="Date de facture"
                          typeDate="date"
                          value={isEditMode ? dayjs(formData.date_facture).format('YYYY-MM-DD') : dayjs(formData.date_facture).format('YYYY-MM-DD')} // Convertir la date en chaîne de caractèresformData.date_facture}
                          dateSelected={(val) => handleDateChange('date_facture', val)}
                          required
                        />
                      </Grid>

                      {/* Statut */}
                      <Grid item xs={12} sm={6} md={3}>
                        <AutocompleteComboBox
                          list={statutOptions}
                          Label="Statut"
                          keyField="value"
                          valueField="label"
                          value={formData.statut}
                          onChange={(selected) => {
                            const value = (selected && selected.value) ? selected.value : selected;
                            handleChange({ target: { name: 'statut', value } });
                          }}
                          widthSpecific="100%"
                        />
                      </Grid>

                      {/* Période début */}
                      <Grid item xs={12} sm={6} md={4}>
                        <DateTimePickerComponent
                          label="Période début"
                          typeDate="date"
                          value={dayjs(formData.periode_debut).format('YYYY-MM-DD')} // Convertir la date en chaîne de caractèresformData.periode_debut}
                          dateSelected={(val) => handleDateChange('periode_debut', val)}
                          required
                        />
                      </Grid>

                      {/* Période fin */}
                      <Grid item xs={12} sm={6} md={4}>
                        <DateTimePickerComponent
                          label="Période fin"
                          typeDate="date"
                          value={dayjs(formData.periode_fin).format('YYYY-MM-DD')} // Convertir la date en chaîne de caractèresformData.periode_fin}
                          dateSelected={(val) => handleDateChange('periode_fin', val)}
                          required
                        />
                      </Grid>

                      {/* Délai de paiement */}
                      <Grid item xs={12} sm={6} md={4}>
                        <TextInputMui
                          label="Délai de paiement (jours)"
                          type="number"
                          value={formData.delai_paiement}
                          setValue={(val) => handleChange({ target: { name: 'delai_paiement', value: Number(val) } })}
                          required={false}
                        />
                      </Grid>

                      {/* Conditions de paiement */}
                      <Grid item xs={12} sm={6}>
                        <TextInputMui
                          label="Conditions de paiement"
                          value={formData.conditions_paiement}
                          setValue={(val) => handleChange({ target: { name: 'conditions_paiement', value: val } })}
                          multiline
                          rows={2}
                        />
                      </Grid>

                      {/* Notes */}
                      <Grid item xs={12} sm={6}>
                        <TextInputMui
                          label="Notes"
                          value={formData.notes}
                          setValue={(val) => handleChange({ target: { name: 'notes', value: val } })}
                          multiline
                          rows={2}
                        />
                      </Grid>

                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Lignes de facturation */}
              <Grid item xs={12}>
                <Card elevation={3} sx={{
                  overflow: 'visible', backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb4b',
                  borderRadius: '32px',
                  padding: { xs: '20px', md: '30px' },
                  marginBottom: '20px',
                  boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)",
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <DescriptionIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="h6" fontWeight="bold">Lignes de facturation</Typography>
                    </Box>

                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      mb: 2,
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 1, sm: 1 }
                    }}>
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>

                        <ButtonAttribute
                          type="button"
                          Bgcolor={alpha(theme.palette.warning.main, 0.1)}
                          color={theme.palette.warning.main}
                          bgColorHover={alpha(theme.palette.warning.main, 0.2)}
                          ColorHover={theme.palette.warning.main}
                          startIcon={isResetting ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                          Label={isResetting ? "Réinitialiser..." : "Réinitialiser voyages"}
                          action={handleResetNonInvoicedVoyages}
                          disabled={isResetting}
                          sx={{ borderRadius: 2 }}
                        />

                        <ButtonAttribute
                          type="button"
                          Bgcolor={alpha(theme.palette.info.main, 0.1)}
                          color={theme.palette.info.main}
                          bgColorHover={alpha(theme.palette.info.main, 0.2)}
                          ColorHover={theme.palette.info.main}
                          startIcon={<AddIcon />}
                          Label="Ajouter des voyages"
                          action={handleOpenAddVoyageDialog}
                          disabled={!formData.ID_Client}
                          sx={{ borderRadius: 2 }}
                        />
                      </Box>

                      <ButtonAttribute
                        type="button"
                        Bgcolor={alpha(theme.palette.success.main, 0.1)}
                        color={theme.palette.success.main}
                        bgColorHover={alpha(theme.palette.success.main, 0.2)}
                        ColorHover={theme.palette.success.main}
                        startIcon={<AddIcon />}
                        Label="Ajouter une ligne"
                        action={handleAddLigne}
                        sx={{ borderRadius: 2 }}
                      />
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    <TableContainer sx={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e5e7eb4b',
                      borderRadius: '32px',
                      padding: { xs: '20px', md: '30px' },
                      marginBottom: '20px',
                      boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)",

                      mb: 3

                    }}>
                      <Table stickyHeader>
                        <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                          <TableRow>
                            <TableCell align="center">N°</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>ID Voyage</TableCell>
                            <TableCell align="right">Quantité</TableCell>
                            <TableCell align="right">Prix unitaire (MAD)</TableCell>
                            <TableCell align="right">TVA (%)</TableCell>
                            <TableCell align="right">TVA 1 (%)</TableCell>
                            <TableCell align="right">Montant HT (MAD)</TableCell>
                            <TableCell align="right">Montant TTC (MAD)</TableCell>
                            <TableCell align="center">Actions</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {formData.lignes.length == 0 ? (
                            <TableRow>
                              <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
                                  <DescriptionIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                                  <Typography variant="body1" color="text.secondary">Aucune ligne de facturation</Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Ajoutez des voyages ou des lignes manuelles
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ) : (
                            formData.lignes.map((ligne, index) => {
                              const isVoyage = !!ligne.ID_Voyage;
                              const isGroupage = isVoyage && ligne.voyage_type === 'Groupage';
                              const isOpen = isVoyage ? openVoyages[ligne.ID_Voyage] : false;
                              const voyageLines = isVoyage ? voyageLinesMap[ligne.ID_Voyage] || [] : [];

                              return (
                                <React.Fragment key={index}>
                                  <TableRow
                                    sx={{
                                      '&:nth-of-type(odd)': { bgcolor: alpha(theme.palette.primary.main, 0.02) },
                                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                                    }}
                                  >
                                    <TableCell align="center">
                                      {ligne.ID_Ligne != null ? Number(ligne.ID_Ligne) + 1 : index + 1}
                                    </TableCell>

                                    <TableCell>
                                      <TextInputMui
                                        value={ligne.description || ''}
                                        setValue={(val) => handleLigneChange(index, 'description', val)}
                                        multiline={false}
                                        type="text"
                                        required
                                      />
                                    </TableCell>

                                    <TableCell>{ligne.ID_Voyage || 'N/A'}</TableCell>

                                    <TableCell align="right">
                                      <TextInputMui
                                        type="number"
                                        value={ligne.quantite}
                                        setValue={(val) => handleLigneChange(index, 'quantite', Number(val))}
                                        required
                                        sx={{ width: '80px' }}
                                      />
                                    </TableCell>

                                    <TableCell align="right">
                                      <TextInputMui
                                        type="number"
                                        value={ligne.prix_unitaire}

                                        setValue={(val) => handleLigneChange(index, 'prix_unitaire', Number(val))}
                                        required
                                        sx={{ width: '120px' }}
                                      />
                                    </TableCell>

                                    <TableCell align="right">
                                      <TextInputMui
                                        type="number"
                                        value={ligne.taux_tva}
                                        setValue={(val) => handleLigneChange(index, 'taux_tva', Number(val))}
                                        required
                                        sx={{ width: '80px' }}
                                      />
                                    </TableCell>
                                    <TableCell align="right">
                                      <TextInputMui

                                        type="number"
                                        value={ligne.taux_tva_1}
                                        setValue={(val) => handleLigneChange(index, 'taux_tva_1', Number(val))}
                                        sx={{ width: '80px' }}
                                      />
                                    </TableCell>

                                    <TableCell align="right">
                                      <Typography fontWeight="medium">
                                        {(ligne.montant_ht ?? 0).toLocaleString(undefined, {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}
                                      </Typography>
                                    </TableCell>

                                    <TableCell align="right">
                                      <Typography fontWeight="medium">
                                        {(ligne.montant_ttc ?? 0).toLocaleString(undefined, {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}
                                      </Typography>
                                    </TableCell>

                                    <TableCell align="center">
                                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                        {isGroupage && (
                                          <IconButton
                                            size="small"
                                            onClick={() => toggleVoyage(ligne.ID_Voyage)}
                                          >
                                            {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                          </IconButton>
                                        )}
                                        <Tooltip title="Supprimer">
                                          <IconButton
                                            onClick={() => handleDeleteLigne(index)}
                                            size="small"
                                            color="error"
                                            sx={{
                                              bgcolor: alpha(theme.palette.error.main, 0.1),
                                              '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2) }
                                            }}
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </TableCell>
                                  </TableRow>

                                  {isGroupage && (
                                    <TableRow
                                      sx={{
                                        '&:nth-of-type(odd)': { bgcolor: alpha(theme.palette.primary.main, 0.02) },
                                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                                      }} >
                                      <TableCell colSpan={10} sx={{ p: 0, border: 0 }}>
                                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                          <Box>
                                            {voyageLines.length > 0 ? (
                                              <Table stickyHeader={true} >
                                                <TableHead>
                                                  <TableRow sx={{ py: 2 }} >
                                                    <TableCell>N°</TableCell>
                                                    <TableCell>Description</TableCell>
                                                    <TableCell>ID Voyage</TableCell>
                                                    <TableCell align="right">Quantité</TableCell>
                                                    <TableCell align="right">Prix unitaire (MAD)</TableCell>
                                                    <TableCell align="right">TVA (%)</TableCell>
                                                    <TableCell align="right">TVA 1 (%)</TableCell>
                                                    <TableCell align="right">Montant HT</TableCell>
                                                    <TableCell align="right">Montant TTC</TableCell>

                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  {voyageLines.map((ligneVoyage, idx) => (
                                                    <TableRow key={idx}>

                                                      <TableCell>{ligneVoyage.id}</TableCell>

                                                      <TableCell>
                                                        <TextInputMui
                                                          size="small"
                                                          sx={{ width: '120px' }}
                                                          type="Text"
                                                          value={(ligneVoyage.destinataire + '-' + ligneVoyage.expediteur)}
                                                          setValue={(val) => handleVoyageLineChange(
                                                            ligne.ID_Voyage,
                                                            idx,
                                                            "destinataire",
                                                            val
                                                          )}
                                                        />
                                                      </TableCell>
                                                      <TableCell>{ligneVoyage.voyage_principal_id}</TableCell>

                                                      <TableCell align="right">
                                                        <TextInputMui
                                                          type="number"
                                                          sx={{ width: '80px' }}
                                                          value={ligneVoyage.quantite}
                                                          setValue={(val) => handleVoyageLineChange(
                                                            ligne.ID_Voyage,
                                                            idx,
                                                            "quantite",
                                                            Number(val)
                                                          )}
                                                        />
                                                      </TableCell>

                                                      <TableCell align="right">
                                                        <TextInputMui
                                                          sx={{ width: '80px' }}
                                                          type="number"
                                                          value={ligneVoyage.prix_unitaire}
                                                          setValue={(val) => handleVoyageLineChange(
                                                            ligne.ID_Voyage,
                                                            idx,
                                                            "prix_unitaire",
                                                            Number(val)
                                                          )}
                                                        />
                                                      </TableCell>

                                                      <TableCell align="right">
                                                        <TextInputMui
                                                          type="number"
                                                          sx={{ width: '80px' }}
                                                          value={ligneVoyage.taux_tva}
                                                          setValue={(val) => handleVoyageLineChange(
                                                            ligne.ID_Voyage,
                                                            idx,
                                                            "taux_tva",
                                                            Number(val)
                                                          )}
                                                        />
                                                      </TableCell>
                                                      <TableCell align="right">
                                                        <TextInputMui
                                                          type="number"
                                                          sx={{ width: '80px' }}
                                                          value={ligneVoyage.taux_tva_1}
                                                          setValue={(val) => handleVoyageLineChange(
                                                            ligne.ID_Voyage,
                                                            idx,
                                                            "taux_tva_1",
                                                            Number(val)
                                                          )}
                                                        />
                                                      </TableCell>

                                                      <TableCell align="right">
                                                        <Typography>
                                                          {ligneVoyage.montant_ht?.toLocaleString(undefined, {
                                                            minimumFractionDigits: 2
                                                          })}
                                                        </Typography>
                                                      </TableCell>

                                                      <TableCell align="right">
                                                        <Typography>
                                                          {ligneVoyage.montant_ttc?.toLocaleString(undefined, {
                                                            minimumFractionDigits: 2
                                                          })}
                                                        </Typography>
                                                      </TableCell>



                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </Table>
                                            ) : (
                                              <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
                                                Aucune ligne pour ce voyage
                                              </Box>
                                            )}
                                          </Box>
                                        </Collapse>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </React.Fragment>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Totaux */}
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                      <Grid container justifyContent="flex-end">
                        <Grid item xs={12} md={4}>
                          <Card elevation={2} sx={{
                            border: '1px solid #e5e7eb4b',
                            borderRadius: '32px',
                            padding: { xs: '20px', md: '30px' },
                            marginBottom: '20px',
                            boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)",
                            background: `linear-gradient(to right, ${alpha(theme.palette.primary.main, 0.05)}, transparent)`
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <AttachMoneyIcon sx={{ mr: 1, color: theme.palette.success.main }} />
                              <Typography variant="h6" fontWeight="bold">Récapitulatif</Typography>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography>Montant HT:</Typography>
                              <Typography fontWeight="bold">
                                {montantHT.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography>TVA:</Typography>
                              <Typography fontWeight="bold">
                                {montantTVA.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD
                              </Typography>
                            </Box>

                            <Divider sx={{ my: 1 }} />

                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="h6" color="primary">Total TTC:</Typography>
                              <Typography variant="h6" fontWeight="bold" color="primary">
                                {montantTTC.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD
                              </Typography>
                            </Box>
                          </Card>
                        </Grid>
                      </Grid>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </form>
          {/* Dialog pour ajouter des voyages */}
          <Dialog
            open={addVoyageDialogOpen}
            onClose={() => setAddVoyageDialogOpen(false)}
            maxWidth="xl"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
              }
            }}
          >
            <DialogTitle sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              display: 'flex',
              alignItems: 'center'
            }}>
              <AddIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
              Ajouter des voyages à la facture
            </DialogTitle>
            <DialogContent dividers>
              <DialogContentText sx={{ mb: 2 }}>
                Sélectionnez les voyages à ajouter à la facture.
              </DialogContentText>

              {voyagesNonFactures.length == 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                  <DescriptionIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Aucun voyage disponible
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                    Il n'y a pas de voyages non facturés pour ce client et cette période.
                  </Typography>
                </Box>
              ) : (
                <TableContainer
                  sx={{
                    maxHeight: 400,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: alpha(theme.palette.divider, 0.5),
                  }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>  </TableCell>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={
                              selectedVoyages.length > 0 &&
                              selectedVoyages.length < voyagesNonFactures.length
                            }
                            checked={
                              voyagesNonFactures.length > 0 &&
                              selectedVoyages.length === voyagesNonFactures.length
                            }
                            onChange={(e) => {
                              e.stopPropagation();
                              setSelectedVoyages(
                                selectedVoyages.length === voyagesNonFactures.length
                                  ? []
                                  : [...voyagesNonFactures]
                              );
                            }}
                          />
                        </TableCell>

                        <TableCell>ID</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Date chargement</TableCell>
                        <TableCell>Date déchargement</TableCell>
                        <TableCell>Expéditeur</TableCell>
                        <TableCell>Destinataire</TableCell>
                        <TableCell align="right">TVA</TableCell>
                        <TableCell align="right">TVA 1</TableCell>
                        <TableCell align="right">Coût Total HT</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {voyagesNonFactures.map((voyage) => {
                        const isSelected = selectedVoyages.some(
                          (v) => v.ID_Voyage == voyage.ID_Voyage
                        );

                        const isOpen = openVoyages[voyage.ID_Voyage];

                        return (
                          <React.Fragment key={voyage.ID_Voyage}>

                            {/* MAIN VOYAGE ROW */}
                            <TableRow hover selected={isSelected}>

                              <TableCell>

                                {voyage.Type_Voyage === "Groupage" && <IconButton
                                  size="small"
                                  onClick={() => toggleVoyage(voyage.ID_Voyage)}
                                >
                                  {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                </IconButton>}
                              </TableCell>

                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() => handleVoyageSelectionChange(voyage)}
                                />
                              </TableCell>

                              <TableCell>{voyage.ID_Voyage}</TableCell>

                              <TableCell>
                                <Chip
                                  label={voyage.Type_Voyage}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              </TableCell>

                              <TableCell>
                                {voyage.Date_chargement
                                  ? new Date(voyage.Date_chargement).toLocaleDateString()
                                  : "N/A"}
                              </TableCell>

                              <TableCell>
                                {voyage.Date_déchargement
                                  ? new Date(voyage.Date_déchargement).toLocaleDateString()
                                  : "N/A"}
                              </TableCell>

                              <TableCell>{voyage.Expediteur}</TableCell>
                              <TableCell>{voyage.Destinataire}</TableCell>

                              <TableCell align="right">
                                {ClientFacture.taux_de_TVA} %
                              </TableCell>
                              <TableCell align="right">
                                {ClientFacture.taux_de_TVA_1} %
                              </TableCell>

                              <TableCell align="right">
                                {voyage.prix_total_voyage?.toLocaleString()} MAD
                              </TableCell>
                            </TableRow>


                            {/* LIGNES VOYAGE */}
                            <TableRow>
                              <TableCell colSpan={12} sx={{ p: 0 }}>
                                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                  <Box sx={{ m: 2 }}>
                                    {lingesVoyagesNonFactures && lingesVoyagesNonFactures.length > 0 ? (
                                      <Table stickyHeader>
                                        <TableHead>
                                          <TableRow>
                                            <TableCell padding="checkbox"></TableCell>
                                            <TableCell>ID Ligne</TableCell>
                                            <TableCell>Expéditeur</TableCell>
                                            <TableCell>Destinataire</TableCell>
                                            <TableCell>Statut</TableCell>
                                            <TableCell>Commentaires</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {lingesVoyagesNonFactures
                                            .filter((ligne) => ligne.voyage_principal_id == voyage.ID_Voyage)
                                            .map((ligne) => (
                                              <TableRow key={ligne.id}>
                                                <TableCell padding="checkbox">
                                                  <Checkbox
                                                    checked={isSelected}
                                                    disabled
                                                  />
                                                </TableCell>
                                                <TableCell>{ligne.id}</TableCell>

                                                <TableCell>{ligne.expediteur}</TableCell>

                                                <TableCell>{ligne.destinataire}</TableCell>

                                                <TableCell>
                                                  <Chip
                                                    label={ligne.statut || "N/A"}
                                                    size="small"
                                                    color="info"
                                                  />
                                                </TableCell>

                                                <TableCell>
                                                  {ligne.commentaires || "-"}
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                        </TableBody>
                                      </Table>
                                    ) : (
                                      <Box
                                        sx={{
                                          textAlign: "center",
                                          py: 3,
                                          color: "text.secondary"
                                        }}
                                      >
                                        Aucune ligne pour ce voyage
                                      </Box>
                                    )}
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>

                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button
                onClick={() => setAddVoyageDialogOpen(false)}
                sx={{
                  borderRadius: 2,
                  px: 3
                }}
              >
                Annuler
              </Button>
              <Button
                onClick={handleAddSelectedVoyages}
                color="primary"
                variant="contained"
                disabled={selectedVoyages.length == 0}
                startIcon={<AddIcon />}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                }}
              >
                Ajouter ({selectedVoyages.length})
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar pour les messages de réinitialisation */}
          <Snackbar
            open={resetSnackbar.open}
            autoHideDuration={6000}
            onClose={() => setResetSnackbar({ ...resetSnackbar, open: false })}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert
              onClose={() => setResetSnackbar({ ...resetSnackbar, open: false })}
              severity={resetSnackbar.severity}
              sx={{ width: '100%' }}
            >
              {resetSnackbar.message}
            </Alert>
          </Snackbar>
        </motion.div>
      </Box>
    </>
  );
};

export default FactureForm;
